import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

const STORAGE_KEY = 'favorite-projects';

/** Stored shape: one key in localStorage, value is accountId -> favorite project ids */
type StoredFavorites = Record<string, string[]>;

@Injectable({
  providedIn: 'root',
})
export class ProjectFavoritesService {
  private readonly favoritesChanged$ = new BehaviorSubject<{ accountId: string } | null>(null);

  /** Emits when favorites change for an account. Components can re-sort lists on this. */
  public get onFavoritesChanged() {
    return this.favoritesChanged$.asObservable();
  }

  public getFavoriteIds(accountId: string): string[] {
    const stored = this.getStored();
    const ids = stored[accountId];
    return Array.isArray(ids) ? ids : [];
  }

  public setFavorite(accountId: string, projectId: string, favorite: boolean): void {
    const stored = this.getStored();
    const ids = this.getFavoriteIds(accountId);
    const set = new Set(ids);
    if (favorite) {
      set.add(projectId);
    } else {
      set.delete(projectId);
    }
    const next = Array.from(set);
    this.setStored({ ...stored, [accountId]: next });
    this.favoritesChanged$.next({ accountId });
  }

  public isFavorite(accountId: string, projectId: string): boolean {
    return this.getFavoriteIds(accountId).includes(projectId);
  }

  /** Toggles favorite state. Returns the new state (true = favorite). */
  public toggleFavorite(accountId: string, projectId: string): boolean {
    const next = !this.isFavorite(accountId, projectId);
    this.setFavorite(accountId, projectId, next);
    return next;
  }

  /** Returns a new array with favorite projects first, then the rest in original order. */
  public sortProjectsByFavorites<T extends { id?: string }>(accountId: string, projects: T[]): T[] {
    const favoriteIds = new Set(this.getFavoriteIds(accountId));
    const favorited: T[] = [];
    const rest: T[] = [];
    for (const p of projects) {
      if (p.id && favoriteIds.has(p.id)) {
        favorited.push(p);
      } else {
        rest.push(p);
      }
    }
    return [...favorited, ...rest];
  }

  public getFavoritesRaw(): string {
    return JSON.stringify(this.getStored());
  }

  public setFavoritesRaw(stored: string): void {
    try {
      const parsed = JSON.parse(stored) as StoredFavorites;
      this.setStored(parsed);
    } catch {
      // ignore invalid raw input
    }
    this.favoritesChanged$.next(null);
  }

  private getStored(): StoredFavorites {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    try {
      const parsed = JSON.parse(raw) as StoredFavorites;
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  private setStored(data: StoredFavorites): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }
}
